import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import { Box, Checkbox, Divider, FormControl, FormControlLabel, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { type ChangeEvent, type FC, type SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { useRequestsContext } from "../../../../contexts/RequestsContext";
import { useSnackbar } from "../../../../contexts/SnackbarContext";
import { useGlobalManualLocationOrder } from "../../../../contexts/Staffing/GlobalManualLocationOrderContext";
import { useJobs } from "../../../../hooks/useJobs";
import { useFormatPersonName } from "../../../../hooks/useNameFormat";
import { useAddSingleShiftByDateAndNurse } from "../../../../pages/Profile/v2/WeeklySchedule/XWeekScheduleGrid/hooks";
import { isNurseActiveInDepartment } from "../../../../utils/formatters";
import { type DepartmentLocation, NotificationType, type Nurse, type OpenShiftRequest } from "../../../../utils/models";
import { compareValues } from "../../../../utils/multiColumnSorting";
import { getUniqueLocations } from "../../../../utils/utilFunctions";
import { useStaffingTargetCheck } from "../../../../utils/warningDialogs/useStaffingTargetCheck";
import { buildEmployeeStatusWarning, useNurseWarnings, WarningDialog } from "../../../../utils/warningDialogs/WarningDialog";
import { useManualAddCompletion } from "../../../RequestData/RequestsTab/useManualAddCompletion";
import { OvertimeCell } from "../../../UIToolkit/Cells/OvertimeCell/OvertimeCell";
import { shouldShowOtColumn } from "../../../UIToolkit/Cells/OvertimeCell/otVisibility";
import { SystemAutoComplete, type SystemAutoCompleteOption } from "../../../UIToolkit/Inputs/SystemAutoComplete/SystemAutoComplete";
import { CommonHeader } from "../../../UIToolkit/molecules/CommonHeader";
import { JobSelector } from "../../../UIToolkit/molecules/JobSelector";
import { ShiftDetailedTimeViewWithPicker } from "../../../UIToolkit/molecules/ShiftDetailedTimeViewWithPicker";
import { ShiftStats } from "../../../UIToolkit/molecules/ShiftStats";
import SystemDialog from "../../../UIToolkit/Popups/Dialog";
import SystemButton from "../../../UIToolkit/SystemButton/SystemButton";
import { useGetNurseExpectedStatsData } from "./useGetNurseExpectedStatsData";

const buttonWidth = 85;

export const AddNurseToShift: FC = () => {
	const navigate = useNavigate();
	const { dateString } = useParams();
	const [searchParams] = useSearchParams();
	const requestId = searchParams.get("requestId");
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const { staffRequests } = useRequestsContext();
	const { completeManualAdd } = useManualAddCompletion();
	const { hospitalData, getWeekIndexByDate, getDayIndexByDate, timezone, chosenDepartment, isMedicalDepartment } = useHospitalData();
	const { customStringOrderMap } = useGlobalManualLocationOrder();
	const enabledJobs = useJobs();
	const [showCloseWarning, setShowCloseWarning] = useState(false);
	const [shiftIndex, setShiftIndex] = useState(0);
	const [shiftStart, setShiftStart] = useState<DateTime>(DateTime.now());
	const [shiftEnd, setShiftEnd] = useState<DateTime>(DateTime.now());
	const [didChangeAnything, setDidChangeAnything] = useState(false);
	const [selectedNurse, setSelectedNurse] = useState<SystemAutoCompleteOption<Nurse> | null>(null);
	const [predictedCost, setPredictedCost] = useState("-");
	const [otRowStats, setOtRowStats] = useState<{ overtimeHours: number; weeklyOvertimeHoursBeforeChange: number | undefined; hasPrediction: boolean }>({
		overtimeHours: 0,
		weeklyOvertimeHoursBeforeChange: undefined,
		hasPrediction: false,
	});
	const [selectedLocation, setSelectedLocation] = useState<SystemAutoCompleteOption<DepartmentLocation> | null>(null);
	const date: DateTime = useMemo(
		() => (dateString ? DateTime.fromISO(dateString).setZone(timezone) : DateTime.invalid("missing date string")),
		[dateString, timezone],
	);
	const weekIndex = useMemo(() => getWeekIndexByDate(date), [date, getWeekIndexByDate]);
	const dayIndex = useMemo(() => getDayIndexByDate(date), [date, getDayIndexByDate]);
	const [isCharge, setIsCharge] = useState(false);
	const [isTrainee, setIsTrainee] = useState(false);
	const [selectedJobId, setSelectedJobId] = useState<string | undefined>(undefined);

	// Both indices are -1 when `date` is outside every loaded week — a stale/shared deep link, a
	// notification link to a date past the staffing window, or a missing/malformed `dateString`. `weeks[-1]`
	// is undefined, so this must not dereference eagerly: it evaluates on the render before the
	// out-of-range effect below has a chance to navigate away.
	const currentShiftDay = useMemo(
		() => hospitalData.weeks[weekIndex]?.weeklyShifts[shiftIndex]?.days[dayIndex],
		[hospitalData.weeks, weekIndex, dayIndex, shiftIndex],
	);

	// Open shift request this screen was opened for (via the "+" on a posted shift card); null for the plain FAB flow.
	const linkedRequest = useMemo<OpenShiftRequest | null>(() => {
		if (!requestId) return null;
		const match = staffRequests.find((request) => request.id === requestId && request.request_type === NotificationType.OpenShift);
		return match ? (match as OpenShiftRequest) : null;
	}, [requestId, staffRequests]);

	const linkedRequestApprovedNurseIds = useMemo<number[]>(
		() => (linkedRequest ? linkedRequest.message.request_data.filter(({ nurse_id }) => !!nurse_id).map(({ nurse_id }) => nurse_id) : []),
		[linkedRequest],
	);

	const lockedTimes = useMemo(
		() => (linkedRequest ? { startTime: linkedRequest.message.shift_start, endTime: linkedRequest.message.shift_end } : undefined),
		[linkedRequest],
	);

	const linkedRequestBonus = linkedRequest ? (linkedRequest.current_bonus ?? linkedRequest.bonus) : null;

	const markDidChangeAnything = useCallback(() => {
		setDidChangeAnything(true);
	}, []);

	const toggleShowCloseWarning = useCallback(() => {
		if (!showCloseWarning && !didChangeAnything) {
			navigate("/");
			return;
		}
		setShowCloseWarning((prev) => !prev);
	}, [showCloseWarning, navigate, didChangeAnything]);

	const performClose = useCallback(() => {
		navigate("/");
	}, [navigate]);

	const updateShiftIndex = useCallback(
		(index: number) => {
			markDidChangeAnything();
			setShiftIndex(index);
		},
		[markDidChangeAnything],
	);

	const filteredNurses = useMemo(
		() =>
			hospitalData.nurses.filter(
				(nurse) =>
					isNurseActiveInDepartment(nurse, chosenDepartment.department_id) &&
					(!linkedRequest || (linkedRequest.message.roles.includes(nurse.role) && !linkedRequestApprovedNurseIds.includes(nurse.id))),
			),
		[hospitalData.nurses, chosenDepartment.department_id, linkedRequest, linkedRequestApprovedNurseIds],
	);

	const formatPersonName = useFormatPersonName();
	const staffMembersForShift = useMemo(
		() =>
			filteredNurses.map<SystemAutoCompleteOption<Nurse>>((nurse) => ({
				value: nurse,
				label: formatPersonName(nurse),
			})),
		[filteredNurses, formatPersonName],
	);

	const locationOptions = useMemo(
		() =>
			getUniqueLocations(chosenDepartment.locations, false)
				.filter((value) => !!value.location)
				.sort((a, b) => compareValues(a.location, b.location, customStringOrderMap))
				.map<SystemAutoCompleteOption<DepartmentLocation>>((departmentLocation) => ({
					value: departmentLocation,
					label: departmentLocation.location,
				})),
		[chosenDepartment, customStringOrderMap],
	);
	const handleSelectedLocationChange = useCallback(
		(_: SyntheticEvent, value: SystemAutoCompleteOption<DepartmentLocation> | null) => {
			setSelectedLocation(value);
			markDidChangeAnything();
		},
		[markDidChangeAnything],
	);

	// Prefill the location from the linked request once, so a later requests refresh doesn't override the user's edits.
	const didPrefillLocation = useRef(false);
	useEffect(() => {
		if (didPrefillLocation.current || !linkedRequest?.location) return;
		const matchingLocation = locationOptions.find((option) => option.value.location === linkedRequest.location);
		if (matchingLocation) {
			didPrefillLocation.current = true;
			setSelectedLocation(matchingLocation);
		}
	}, [linkedRequest, locationOptions]);

	const createDisabled = useMemo(() => {
		return !selectedNurse || !shiftStart.isValid || !shiftEnd.isValid || shiftStart > shiftEnd;
	}, [selectedNurse, shiftStart, shiftEnd]);

	const { addShift, isLoading } = useAddSingleShiftByDateAndNurse({
		selectedDate: date,
		// For the linked-request flow we navigate ourselves, after the request is decremented/deleted.
		onCloseCalled: linkedRequest ? undefined : performClose,
	});

	const { getExpectedStats } = useGetNurseExpectedStatsData({
		weekIndex,
		dayIndex,
		shiftIndex,
	});

	const performActualAddShift = useCallback(async () => {
		if (requestId && !linkedRequest) {
			// The request this screen was opened for no longer exists (filled or deleted meanwhile) — fail loud instead of adding without updating it.
			showSnackbar(t("open.shift.request.not.found"), "error");
			return;
		}
		const added = await addShift(selectedNurse?.value, shiftStart, shiftEnd, selectedLocation?.value.location, isCharge, isTrainee, selectedJobId);
		if (added && linkedRequest) {
			await completeManualAdd({
				request: linkedRequest,
				approvedNursesCount: linkedRequestApprovedNurseIds.length,
				source: "MobileAddNurseToShift",
			});
			performClose();
		}
	}, [
		selectedNurse?.value,
		addShift,
		shiftStart,
		shiftEnd,
		selectedLocation,
		isCharge,
		isTrainee,
		selectedJobId,
		requestId,
		linkedRequest,
		linkedRequestApprovedNurseIds,
		completeManualAdd,
		performClose,
		showSnackbar,
		t,
	]);
	const performActualAddShiftRef = useRef(performActualAddShift);
	useEffect(() => {
		performActualAddShiftRef.current = performActualAddShift;
	}, [performActualAddShift]);

	const getTargetCheck = useStaffingTargetCheck();
	const [
		warningState,
		checkWarnings,
		handleAgencyConfirm,
		handleOvertimeConfirm,
		handleDailyHoursConfirm,
		handleCancelWarnings,
		,
		handleTargetConfirm,
		handleEmployeeStatusConfirm,
	] = useNurseWarnings(
		() => performActualAddShiftRef.current(), //solves stale function issue where an old version of performActualAddShift is called
		chosenDepartment.longShiftDuration,
	);

	const handleExpectedStats = useCallback(
		async (nurse: Nurse, shiftStartTime: DateTime, shiftEndTime: DateTime, markWarnings?: boolean) => {
			if (nurse) {
				const expectedStats = await getExpectedStats({
					selectedNurse: nurse,
					shiftStart: shiftStartTime,
					shiftEnd: shiftEndTime,
					jobId: selectedJobId,
				});
				if (markWarnings) {
					const targetWarning = getTargetCheck({ locationName: selectedLocation?.value.location, weekIndex, dayNo: dayIndex, shiftIndex, isActual: false });
					checkWarnings(
						expectedStats.isAgencyNurse,
						expectedStats.overtimeHours,
						expectedStats.overtimeCost,
						expectedStats.dailyHours,
						false,
						false,
						targetWarning,
						buildEmployeeStatusWarning(nurse, chosenDepartment.department_id, shiftStartTime),
					);
				}
				setPredictedCost(expectedStats.predictedCost || "-");
				setOtRowStats({
					overtimeHours: expectedStats.overtimeHours,
					weeklyOvertimeHoursBeforeChange: expectedStats.weeklyOvertimeHoursBeforeChange,
					hasPrediction: expectedStats.hasPrediction,
				});
			}
		},
		[getExpectedStats, checkWarnings, getTargetCheck, selectedJobId, selectedLocation, weekIndex, dayIndex, shiftIndex, chosenDepartment.department_id],
	);

	const getNurseJobs = useCallback(
		(nurse: Nurse) => {
			if (nurse.jobs?.length) return nurse.jobs;
			if (nurse.jobIds?.length) return enabledJobs.filter((j) => nurse.jobIds?.includes(j.id));
			return enabledJobs;
		},
		[enabledJobs],
	);

	const handleSelectedNurseChange = useCallback(
		(_: SyntheticEvent, value: SystemAutoCompleteOption<Nurse> | null) => {
			setSelectedNurse(value);
			const jobs = value?.value ? getNurseJobs(value.value) : [];
			const requestJob = linkedRequest?.job_id ? jobs.find((job) => job.id === linkedRequest.job_id) : undefined;
			setSelectedJobId(requestJob ? requestJob.id : jobs[0]?.id);
			markDidChangeAnything();
		},
		[markDidChangeAnything, getNurseJobs, linkedRequest],
	);

	useEffect(() => {
		if (selectedNurse) {
			handleExpectedStats(selectedNurse.value, shiftStart, shiftEnd, false);
		}
	}, [selectedNurse, handleExpectedStats, shiftStart, shiftEnd]);

	const handleMakeChargeChange = useCallback(
		(event: ChangeEvent<HTMLInputElement>) => {
			setIsCharge(event.target.checked);
			markDidChangeAnything();
		},
		[markDidChangeAnything],
	);

	const handleMakeTraineeChange = useCallback(
		(event: ChangeEvent<HTMLInputElement>) => {
			setIsTrainee(event.target.checked);
			markDidChangeAnything();
		},
		[markDidChangeAnything],
	);

	const handleCreateClicked = useCallback(async () => {
		if (!selectedNurse || !selectedNurse.value) {
			return;
		}
		handleExpectedStats(selectedNurse.value, shiftStart, shiftEnd, true);
	}, [selectedNurse, handleExpectedStats, shiftStart, shiftEnd]);

	// There is no shift to add to when the date resolves to no loaded week, so say why and hand the user
	// back to staffing, which clamps onto a loaded day. A populated `weeks` array is what separates "this
	// date isn't in the schedule" from "the schedule hasn't arrived yet", matching MobileStaffingHeader.
	const isDateOutOfRange = !currentShiftDay && !!hospitalData.weeks.length;
	useEffect(() => {
		if (!isDateOutOfRange) return;
		showSnackbar(t("staffing.date.out.of.range"), "error");
		navigate(`/${chosenDepartment.department_id}/staffing`);
	}, [isDateOutOfRange, showSnackbar, t, navigate, chosenDepartment.department_id]);

	// Bail after every hook so hook order stays stable across the resolvable/unresolvable transition. This
	// also narrows `currentShiftDay` for the header below.
	if (!currentShiftDay) return null;

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				width: "100%",
				height: "calc(100% - 10px)",
				justifyContent: "space-between",
				bgcolor: "white",
				p: 2,
				borderRadius: 2,
				overflow: "auto",
				boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
			}}
		>
			<CommonHeader
				title={"Add Nurse"}
				endingContent={
					<Typography
						sx={{
							color: "primary.main",
							fontSize: "14px",
						}}
					>
						{currentShiftDay.date.toFormat("MMM D, yyyy")}
					</Typography>
				}
				onClose={toggleShowCloseWarning}
			/>
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					width: "100%",
					height: "100%",
					justifyContent: "space-between",
					overflow: "auto",
				}}
			>
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "start",
						gap: 1,
						width: "100%",
						py: 1,
					}}
				>
					{showCloseWarning && (
						<SystemDialog
							open={true}
							onClose={toggleShowCloseWarning}
							title={"Discard Changes"}
							body={"Discard adding nurse to shift?"}
							confirm={"Discard"}
							onConfirm={performClose}
						/>
					)}

					<ShiftStats shiftIndex={shiftIndex} date={date} />
					{!!linkedRequestBonus && (
						<Box
							data-testid="add-nurse-to-shift-bonus"
							sx={{
								width: "100%",
								display: "flex",
								alignItems: "center",
								gap: 0.5,
							}}
						>
							<MonetizationOnIcon sx={{ color: "warning.main" }} />
							<Typography sx={{ fontWeight: 600 }}>
								${linkedRequestBonus} {t("pickup.bonus")}
							</Typography>
						</Box>
					)}
					<Box
						sx={{
							width: "100%",
							paddingBlock: 1,
						}}
					>
						<Divider sx={{ width: "100%" }} />
					</Box>
					<SystemAutoComplete
						fullWidth
						options={staffMembersForShift}
						value={selectedNurse}
						onChange={handleSelectedNurseChange}
						sx={{
							width: "100%",
							mt: 1,
						}}
						textFieldProps={{
							formControlSx: {
								width: "100%",
							},
							label: "Staff Member",
							placeholder: "Start typing to search for a nurse",
							reserveHeightForHelperText: false,
							required: true,
						}}
					/>
					<SystemAutoComplete
						fullWidth
						options={locationOptions}
						value={selectedLocation}
						onChange={handleSelectedLocationChange}
						sx={{
							width: "100%",
						}}
						textFieldProps={{
							formControlSx: {
								width: "100%",
							},
							label: "Location",
							placeholder: "Start typing to search for a location",
							reserveHeightForHelperText: false,
						}}
					/>
					{!!selectedNurse?.value && (
						<Box
							sx={{
								width: "100%",
								display: "flex",
								alignItems: "center",
								justifyContent: "start",
							}}
						>
							<Typography>
								Predicted Cost:{" "}
								<strong>
									{predictedCost}
									{linkedRequestBonus ? ` (+$${linkedRequestBonus})` : ""}
								</strong>
							</Typography>
						</Box>
					)}
					{!!selectedNurse?.value && shouldShowOtColumn({ hasOvertimeData: otRowStats.hasPrediction, isMedicalDepartment }) && (
						<Box sx={{ width: "100%", display: "flex", alignItems: "baseline", justifyContent: "start", gap: 1 }}>
							<Typography>{t("nurses.matrix.ot")}:</Typography>
							<OvertimeCell
								overtimeHours={otRowStats.overtimeHours}
								weeklyOvertimeHoursBeforeChange={otRowStats.weeklyOvertimeHoursBeforeChange}
								showWeeklyOt={otRowStats.weeklyOvertimeHoursBeforeChange != null}
								isAdd
								detailed
								t={t}
							/>
						</Box>
					)}
					<FormControl sx={{ width: "100%" }}>
						<FormControlLabel control={<Checkbox onChange={handleMakeChargeChange} />} label={"Make Charge"} sx={{ width: "100%" }} />
						<FormControlLabel control={<Checkbox onChange={handleMakeTraineeChange} />} label={"Make Trainee"} sx={{ width: "100%" }} />
					</FormControl>
					{selectedNurse?.value && (
						<Box sx={{ width: "100%" }}>
							<Typography sx={{ color: "primary.main", mb: 0.5, fontSize: "13px" }}>Job</Typography>
							<JobSelector
								jobs={getNurseJobs(selectedNurse.value)}
								selectedJobIds={selectedJobId ? [selectedJobId] : []}
								onSelectedJobsChanged={(ids) => setSelectedJobId(ids[0])}
								singleSelect
								hideAllOption
								variant="dropdown"
							/>
						</Box>
					)}

					<Box
						sx={{
							width: "100%",
							paddingBlock: 1,
						}}
					>
						<Divider sx={{ width: "100%" }} />
					</Box>

					<ShiftDetailedTimeViewWithPicker
						shiftIndex={shiftIndex}
						setShiftIndex={updateShiftIndex}
						selectedRoleForShift={selectedNurse?.value?.role}
						shiftStart={shiftStart}
						shiftEnd={shiftEnd}
						setShiftStart={setShiftStart}
						setShiftEnd={setShiftEnd}
						date={date}
						roleSource={"nurse"}
						selectedLocation={selectedLocation?.value.location}
						lockedTimes={lockedTimes}
						jobId={selectedJobId}
					/>
				</Box>
				<Box
					sx={{
						display: "flex",
						justifyContent: "end",
						gap: 2,
						mt: 2,
					}}
				>
					<SystemButton buttonVariant={"secondary"} sx={{ width: buttonWidth }} onClick={toggleShowCloseWarning}>
						Cancel
					</SystemButton>
					<SystemButton buttonVariant={"primary"} sx={{ width: buttonWidth }} disabled={createDisabled} loading={isLoading} onClick={handleCreateClicked}>
						Create
					</SystemButton>
				</Box>

				<WarningDialog
					openTargetWarning={warningState.isTargetWarningOpen}
					openAgencyWarning={warningState.isAgencyWarningOpen}
					openOvertimeWarning={warningState.isOvertimeWarningOpen}
					openDailyHoursWarning={warningState.isDailyHoursWarningOpen}
					openEmployeeStatusWarning={warningState.isEmployeeStatusWarningOpen}
					overtimeDetails={warningState.overtimeDetails}
					dailyHoursDetails={warningState.dailyHoursDetails}
					targetDetails={warningState.targetDetails}
					employeeStatusDetails={warningState.employeeStatusDetails}
					longShiftDuration={chosenDepartment.longShiftDuration}
					onCancelTarget={handleCancelWarnings}
					onConfirmTarget={handleTargetConfirm}
					onCancelAgency={handleCancelWarnings}
					onConfirmAgency={handleAgencyConfirm}
					onCancelOvertime={handleCancelWarnings}
					onConfirmOvertime={handleOvertimeConfirm}
					onCancelDailyHours={handleCancelWarnings}
					onConfirmDailyHours={handleDailyHoursConfirm}
					onCancelEmployeeStatus={handleCancelWarnings}
					onConfirmEmployeeStatus={handleEmployeeStatusConfirm}
				/>
			</Box>
		</Box>
	);
};
